/**
 * Setup Status Utilities
 * 
 * This module provides utilities to calculate the admin's setup completion status
 * based on the number of content types they have created (news, surveys, events, coupons).
 */

export interface OwnCreated {
  news: number;
  surveys: number;
  events: number;
  coupons: number;
}

/**
 * Setup status determines which sections should be displayed on the Home page:
 * - 'needs-get-started': 3-4 content types missing (show Get Started section)
 * - 'needs-finish-setup': 1-2 content types missing (show Finish Your Setup section)
 * - 'completed': All content types created (hide setup sections)
 */
export type SetupStatus = 
  | 'needs-get-started'    // 3-4 missing (show Get Started)
  | 'needs-finish-setup'   // 1-2 missing (show Finish Setup)
  | 'completed';           // 0 missing (hide both)

/**
 * Content type mapping from API to setup items
 */
export const CONTENT_TYPE_MAP: Record<keyof OwnCreated, string> = {
  'news': 'add-news',
  'surveys': 'create-survey',
  'events': 'add-event',
  'coupons': 'add-gift-cards',
};

/**
 * Calculate setup status based on content creation counts
 * 
 * Business Logic:
 * - Count how many content types have 0 items (not created)
 * - If 3 or 4 missing → 'needs-get-started'
 * - If 1 or 2 missing → 'needs-finish-setup'
 * - If 0 missing → 'completed'
 * 
 * @param ownCreated - Object with counts for each content type
 * @returns Setup status determining which sections to show
 */
export const calculateSetupStatus = (ownCreated: OwnCreated): SetupStatus => {
  // Count how many categories have 0 items
  const missingCount = Object.values(ownCreated).filter(count => count === 0).length;
  
  // Apply business logic based on missing count
  if (missingCount >= 3) {
    return 'needs-get-started';
  } else if (missingCount >= 1) {
    return 'needs-finish-setup';
  } else {
    return 'completed';
  }
};

/**
 * Get missing setup items for Finish Your Setup section
 * Returns the setup item IDs for content types that haven't been created yet
 * 
 * @param ownCreated - Object with counts for each content type
 * @returns Array of setup item IDs for missing content types
 */
export const getMissingItems = (ownCreated: OwnCreated): string[] => {
  const missingItems: string[] = [];
  
  // Check each content type
  (Object.keys(ownCreated) as (keyof OwnCreated)[]).forEach(key => {
    if (ownCreated[key] === 0) {
      // Map API key to setup item ID
      missingItems.push(CONTENT_TYPE_MAP[key]);
    }
  });
  
  return missingItems;
};

/**
 * Get default own_created object for error/fallback scenarios
 */
export const getDefaultOwnCreated = (): OwnCreated => ({
  news: 0,
  surveys: 0,
  events: 0,
  coupons: 0,
});

